<template>
  <div
    class="min-h-screen pb-24"
    :style="{ backgroundColor: branding?.backgroundColor || '#F3F4F6' }"
  >
    <!-- Header -->
    <nav
      class="fixed top-0 w-full z-50 backdrop-blur-md border-b px-4 py-3 flex items-center justify-between"
      :style="{
        backgroundColor: (branding?.backgroundColor || '#F3F4F6') + 'E6',
        borderColor: branding?.backgroundColor || '#F3F4F6',
      }"
    >
      <button
        class="p-2 rounded-full hover:opacity-80 transition"
        @click="goToHub"
      >
        <c-icon
          name="ArrowBackRound"
          size="1.5rem"
          class="text-contrast-background"
        />
      </button>
      <span class="font-semibold text-lg text-contrast-background">{{
        $t("profile.my-profile")
      }}</span>
      <div class="w-10"></div>
    </nav>

    <!-- Main Content -->
    <main class="pt-20 px-4 max-w-md mx-auto" v-if="customer">
      <!-- Profile Card -->
      <div
        class="bg-white rounded-3xl p-6 shadow-lg mb-6 relative overflow-hidden"
      >
        <!-- Decorative circles -->
        <div
          class="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-2xl opacity-20"
          :style="{ backgroundColor: accentColor }"
        ></div>
        <div
          class="absolute -bottom-10 -left-10 w-24 h-24 rounded-full blur-2xl opacity-20"
          :style="{ backgroundColor: accentColor }"
        ></div>

        <div class="flex flex-col items-center">
          <!-- Avatar -->
          <div class="relative w-32 h-32 mb-4">
            <div
              class="w-full h-full rounded-full overflow-hidden border-4 border-white shadow-lg flex items-center justify-center text-4xl text-contrast-background font-bold"
              :style="{ backgroundColor: avatarColor }"
            >
              {{ customerInitials }}
            </div>
            <!-- Level badge -->
            <div
              v-if="customerLevel"
              class="absolute bottom-0 right-0 text-contrast text-xs font-bold px-2 py-1 rounded-full border-2 border-white shadow-sm flex items-center gap-1"
              :style="{ backgroundColor: accentColor }"
            >
              <c-icon name="StarRound" size="0.7rem" />
              {{ customerLevel.name }}
            </div>
          </div>

          <!-- Name -->
          <h1 class="text-2xl font-bold text-center mb-1">
            {{ customer.name }} {{ customer.surname || "" }}
          </h1>

          <!-- Member since -->
          <p class="text-gray-400 text-sm mb-4">
            {{ $t("profile.member-since") }}
            {{ $filters.date(customer.creationTime) }}
          </p>

          <!-- Stats grid -->
          <div class="grid grid-cols-3 gap-4 w-full mt-2">
            <div
              v-for="(stat, index) in displayStats"
              :key="index"
              class="text-center p-3 bg-gray-100 rounded-xl"
            >
              <span class="block text-blue text-2xl font-bold">{{
                stat.value
              }}</span>
              <span
                class="text-xs text-gray-400 font-medium uppercase tracking-wide"
                >{{ stat.label }}</span
              >
            </div>
          </div>

          <!-- Points Expiration Warnings -->
          <div v-if="pointExpirations.length > 0" class="w-full mt-4 space-y-2">
            <div
              v-for="expiration in pointExpirations"
              :key="expiration.pointTypeId"
              class="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
              :class="
                expiration.daysRemaining <= 7
                  ? 'bg-red-50 text-red-600'
                  : 'bg-amber-50 text-amber-600'
              "
            >
              <c-icon name="HourglassTopFilled" size="0.9rem" />
              <span>
                {{
                  $t("profile.points-expire-in", {
                    days: expiration.daysRemaining,
                    name: expiration.pointTypeName,
                  })
                }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Available Coupons Section -->
      <div v-if="availableCoupons?.length > 0" class="mb-6">
        <h2 class="font-semibold text-lg text-contrast-background mb-4 px-1">
          {{ $t("profile.available-coupons") }}
        </h2>
        <div class="space-y-3">
          <div
            v-for="coupon in availableCoupons"
            :key="coupon.id"
            class="bg-white rounded-2xl p-4 shadow-sm border border-gray-200 flex items-center justify-between"
          >
            <div class="flex items-center">
              <div
                class="w-10 h-10 rounded-full flex items-center justify-center mr-3"
                :style="{ backgroundColor: accentColor + '20' }"
              >
                <c-icon
                  name="CardGiftcardRound"
                  size="1.2rem"
                  :style="{ color: accentColor }"
                />
              </div>
              <div>
                <h3 class="font-bold text-sm">
                  {{ promotionsById[coupon.promotionId]?.name }}
                </h3>
                <p v-if="coupon.validUntil" class="text-xs text-gray-400">
                  {{ $t("profile.valid-until") }}
                  {{ $filters.date(coupon.validUntil) }}
                </p>
              </div>
            </div>
            <div
              class="px-3 py-1 rounded-full text-xs font-medium text-contrast-background"
              :style="{ backgroundColor: accentColor }"
            >
              {{ $t("profile.active") }}
            </div>
          </div>
        </div>
      </div>

      <!-- Rewards List Section -->
      <div v-if="redeemableRewards?.length > 0" class="mb-6">
        <h2 class="font-semibold text-lg text-contrast-background mb-4 px-1">
          {{ $t("profile.rewards-list") }}
        </h2>
        <div class="space-y-3">
          <div
            v-for="reward in redeemableRewards"
            :key="reward.id"
            class="bg-white rounded-2xl p-4 shadow-sm border border-gray-200"
          >
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center">
                <div
                  class="w-10 h-10 rounded-full flex items-center justify-center mr-3"
                  :style="{
                    backgroundColor: reward.canRedeem
                      ? accentColor + '20'
                      : '#F3F4F6',
                  }"
                >
                  <c-icon
                    name="EmojiEventsRound"
                    size="1.2rem"
                    :style="{
                      color: reward.canRedeem ? accentColor : '#9CA3AF',
                    }"
                  />
                </div>
                <div>
                  <h3 class="font-bold text-sm">{{ reward.name }}</h3>
                  <p class="text-xs text-gray-400">
                    {{ reward.pointsRequired }} {{ reward.pointTypeName }}
                  </p>
                </div>
              </div>
              <div
                v-if="reward.canRedeem"
                class="px-3 py-1 rounded-full text-xs font-medium text-contrast-background"
                :style="{ backgroundColor: '#2DD4BF' }"
              >
                {{ $t("profile.available") }}
              </div>
              <div v-else class="text-xs text-gray-400 text-right">
                {{ reward.pointsNeeded }} {{ $t("profile.points-to-go") }}
              </div>
            </div>
            <!-- Progress bar -->
            <div class="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full transition-all duration-500"
                :style="{
                  width: reward.progress + '%',
                  backgroundColor: reward.canRedeem ? '#2DD4BF' : accentColor,
                }"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Activity Section -->
      <div v-if="recentActivity?.length > 0" class="mb-6">
        <h2 class="font-semibold text-contrast-background text-lg mb-4 px-1">
          {{ $t("profile.recent-activity") }}
        </h2>
        <div class="relative ml-3">
          <!-- Vertical line -->
          <div class="absolute left-3 top-3 bottom-3 w-0.5 bg-gray-200"></div>
          <div class="space-y-4">
            <div
              v-for="(activity, index) in recentActivity"
              :key="index"
              class="relative flex items-start"
            >
              <!-- Circle indicator -->
              <div
                class="relative z-10 flex-shrink-0 w-6 h-6 rounded-full border-4 border-white flex items-center justify-center shadow-sm"
                :style="{ backgroundColor: activity.color }"
              >
                <c-icon
                  :name="activity.icon"
                  size="0.7rem"
                  class="text-contrast-background"
                />
              </div>
              <!-- Content card -->
              <div
                class="ml-4 flex-1 bg-white rounded-2xl p-4 shadow-sm border border-gray-200"
                :class="{ 'opacity-60': index > 1 }"
              >
                <div class="flex justify-between items-start mb-1">
                  <div>
                    <h3 class="font-bold text-sm">
                      {{ activity.title }}
                    </h3>
                    <p class="text-xs text-gray-400">{{ activity.subtitle }}</p>
                  </div>
                  <span
                    class="text-[10px] bg-gray-100 px-2 py-1 rounded text-gray-400"
                  >
                    {{ activity.timeAgo }}
                  </span>
                </div>
                <div
                  v-if="activity.tags?.length"
                  class="flex items-center gap-2 mt-2"
                >
                  <span
                    v-for="(tag, tagIndex) in activity.tags"
                    :key="tagIndex"
                    class="text-xs font-medium px-2 py-0.5 rounded"
                    :style="{
                      backgroundColor: tag.color + '20',
                      color: tag.color,
                    }"
                  >
                    {{ tag.text }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Scan Ticket Button -->
      <div v-if="organizationConfig?.scanOwnTickets" class="mt-6 mb-4">
        <n-button
          type="primary"
          class="w-full"
          size="large"
          @click="showAddOrderTicket = true"
        >
          <template #icon>
            <c-icon name="ReceiptRound" size="1.2rem" />
          </template>
          {{ $t("orders.scan-ticket") }}
        </n-button>
      </div>

      <!-- Personal Info Section -->
      <div class="mb-6">
        <div class="flex items-center justify-between mb-4 px-1">
          <h2 class="font-semibold text-lg text-contrast-background">
            {{ $t("profile.personal-info") }}
          </h2>
          <button
            class="flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-full transition"
            :style="{ color: accentColor, backgroundColor: accentColor + '15' }"
            @click="openEditModal"
          >
            <c-icon name="EditRound" size="0.9rem" />
            {{ $t("profile.edit") }}
          </button>
        </div>
        <div
          class="bg-white rounded-2xl p-4 shadow-sm border border-gray-200 space-y-3"
        >
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-400">{{ $t("profile.name") }}</span>
            <span class="text-sm font-medium">{{ customer.name }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-400">{{
              $t("profile.surname")
            }}</span>
            <span class="text-sm font-medium">{{
              customer.surname || "-"
            }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-400">{{
              $t("profile.birthdate")
            }}</span>
            <span class="text-sm font-medium">{{
              customer.birthDate ? $filters.date(customer.birthDate) : "-"
            }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-400">{{ $t("profile.email") }}</span>
            <span class="text-sm font-medium break-all">{{
              customer.email || "-"
            }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-400">{{
              $t("profile.phone-number")
            }}</span>
            <span class="text-sm font-medium">{{
              customer.phoneNumber || "-"
            }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-400">{{
              $t("profile.marketing-communication")
            }}</span>
            <span class="text-sm font-medium">{{
              customer.marketingCommunication
                ? $t("profile.yes")
                : $t("profile.no")
            }}</span>
          </div>
        </div>
      </div>

      <!-- Download Pass Button -->
      <div class="mt-4 mb-6">
        <n-button
          type="primary"
          class="w-full"
          size="large"
          @click="handleDownloadPass"
        >
          <c-icon name="FileDownloadOutlined" size="1.2rem" />
          {{ $t("profile.download-pass") }}
        </n-button>
      </div>
    </main>

    <!-- Edit Profile Modal -->
    <n-modal
      v-model:show="showEditModal"
      preset="card"
      :title="$t('profile.edit-profile')"
      class="max-w-md mx-4"
      :mask-closable="false"
    >
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">{{
            $t("profile.name")
          }}</label>
          <n-input
            v-model:value="editForm.name"
            :placeholder="$t('profile.name')"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">{{
            $t("profile.surname")
          }}</label>
          <n-input
            v-model:value="editForm.surname"
            :placeholder="$t('profile.surname')"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">{{
            $t("profile.birthdate")
          }}</label>
          <n-date-picker
            v-model:value="editForm.birthDate"
            type="date"
            class="w-full"
            clearable
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">{{
            $t("profile.email")
          }}</label>
          <n-input
            v-model:value="editForm.email"
            :placeholder="$t('profile.email')"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">{{
            $t("profile.phone-number")
          }}</label>
          <vue-tel-input
            v-model="editForm.phoneNumber"
            :inputOptions="{ placeholder: $t('profile.phone-number') }"
            :mode="'international'"
            :styleClasses="['w-full', 'h-9']"
            :defaultCountry="'ES'"
          />
        </div>
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-gray-600">{{
            $t("profile.marketing-communication")
          }}</label>
          <n-switch v-model:value="editForm.marketingCommunication" />
        </div>
      </div>
      <template #footer>
        <div class="flex gap-3 justify-end">
          <n-button @click="showEditModal = false" :disabled="saving">{{
            $t("cancel")
          }}</n-button>
          <n-button type="primary" @click="saveProfile" :loading="saving">{{
            $t("profile.save")
          }}</n-button>
        </div>
      </template>
    </n-modal>

    <add-order-ticket
      v-if="showAddOrderTicket"
      :customer-id="customerId"
      @close="showAddOrderTicket = false"
      @order-added="handleOrderAdded"
    />

    <c-loading-screen v-if="loading" />
  </div>
</template>

<script>
import api from "@/api.js";
import { mapState, mapActions } from "pinia";
import { authStore } from "@/stores/auth";
import CIcon from "../../../core-ui/src/components/CIcon.vue";
import CLoadingScreen from "../../../core-ui/src/components/CLoadingScreen.vue";
import AddOrderTicket from "./AddOrderTicket.vue";
import utils from "@comeback/core/src/utils.js";
import moment from "moment";

export default {
  name: "CustomerProfilePage",
  components: {
    CIcon,
    CLoadingScreen,
    AddOrderTicket,
  },
  data() {
    return {
      customer: null,
      customerPoints: [],
      customerPointsLog: [],
      customerLevel: null,
      appliedPromotions: [],
      assignedPromotions: [],
      visits: 0,
      pointTypes: [],
      promotions: [],
      loading: false,
      showAddOrderTicket: false,
      showEditModal: false,
      saving: false,
      editForm: {
        name: "",
        surname: "",
        birthDate: null,
        email: "",
        phoneNumber: "",
        marketingCommunication: false,
      },
    };
  },
  async mounted() {
    if (!this.customerId) {
      this.$router.replace({ name: "customerHub" });
      return;
    }
    await this.fetchCustomerProfile();
  },
  computed: {
    ...mapState(authStore, [
      "branding",
      "organization",
      "passId",
      "organizationConfig",
      "currentCustomer",
    ]),
    customerId() {
      return this.currentCustomer?.id;
    },
    accentColor() {
      return this.branding?.accentColor || "#6B9AE1";
    },
    avatarColor() {
      if (this.customer?.name) {
        return utils.getHashColor(this.customer.name);
      }
      return this.accentColor;
    },
    customerInitials() {
      if (!this.customer?.name) return "";
      return (
        this.customer.name[0] + (this.customer.surname?.[0] || "")
      ).toUpperCase();
    },
    customerPointsByTypeId() {
      return this.customerPoints.reduce((acc, point) => {
        acc[point.pointTypeId] = point.points;
        return acc;
      }, {});
    },
    displayStats() {
      const stats = [];

      // Add point types
      this.pointTypes.forEach((pt) => {
        const points = this.customerPointsByTypeId[pt.id] || 0;
        if (pt.format === "cash") {
          stats.push({
            value: this.$filters.currency(points),
            label: pt.name,
            color: this.accentColor,
          });
          return;
        } else {
          stats.push({
            value: points,
            label: pt.name,
            color: this.accentColor,
          });
        }
      });

      // Add visits if > 0
      if (this.visits > 0) {
        stats.push({
          value: this.visits,
          label: this.$t("profile.visits"),
          color: "#8B5CF6",
        });
      }

      // Add active coupons count
      const activeCoupons = this.availableCoupons?.length || 0;
      if (activeCoupons > 0) {
        stats.push({
          value: activeCoupons,
          label: this.$t("profile.rewards"),
          color: "#2DD4BF",
        });
      }

      return stats.slice(0, 3);
    },
    availableCoupons() {
      return this.assignedPromotions.filter((ap) => {
        if (ap.redeemed) return false;
        if (ap.validUntil && new Date(ap.validUntil) < new Date()) return false;
        return true;
      });
    },
    pointExpirations() {
      const expirations = [];
      this.pointTypes.forEach((pt) => {
        if (!pt.expirationDays) return;
        const currentPoints = this.customerPointsByTypeId[pt.id] || 0;
        if (currentPoints <= 0) return;
        // Find the most recent log entry for this point type
        const lastLog = this.customerPointsLog.find(
          (log) => log.pointTypeId === pt.id
        );
        if (!lastLog) return;
        const lastActivityDate = new Date(lastLog.creationTime);
        const expirationDate = new Date(lastActivityDate);
        expirationDate.setDate(expirationDate.getDate() + pt.expirationDays);
        const now = new Date();
        const daysRemaining = Math.ceil(
          (expirationDate - now) / (1000 * 60 * 60 * 24)
        );
        if (daysRemaining > 0) {
          expirations.push({
            pointTypeId: pt.id,
            pointTypeName: pt.name,
            daysRemaining,
            expirationDate,
          });
        }
      });
      return expirations;
    },
    redeemableRewards() {
      // Get point types with format "points" or "stamps"
      const validPointTypes = this.pointTypes.filter(
        (pt) => pt.format === "points" || pt.format === "stamps"
      );
      if (validPointTypes.length === 0) return [];

      const validPointTypeIds = validPointTypes.map((pt) => pt.id);

      // Get promotions that require points from valid point types
      return this.promotions
        .filter(
          (p) =>
            p.pointsRequired > 0 &&
            p.enabled &&
            p.type !== "level" &&
            validPointTypeIds.includes(p.pointTypeId)
        )
        .map((p) => {
          const pointType = this.pointTypes.find(
            (pt) => pt.id === p.pointTypeId
          );
          const currentPoints = this.customerPointsByTypeId[p.pointTypeId] || 0;
          const canRedeem = currentPoints >= p.pointsRequired;
          const progress = Math.min(
            (currentPoints / p.pointsRequired) * 100,
            100
          );
          return {
            ...p,
            pointTypeName: pointType?.name || this.$t("profile.points"),
            currentPoints,
            canRedeem,
            progress,
            pointsNeeded: Math.max(p.pointsRequired - currentPoints, 0),
          };
        })
        .filter((r) => r.currentPoints > 0)
        .sort((a, b) => a.pointsRequired - b.pointsRequired);
    },
    promotionsById() {
      return this.promotions.reduce((acc, p) => {
        acc[p.id] = p;
        return acc;
      }, {});
    },
    recentActivity() {
      const activities = [];

      // Add points log entries
      this.customerPointsLog.slice(0, 5).forEach((log) => {
        const pointType = this.pointTypes.find(
          (pt) => pt.id === log.pointTypeId
        );
        const points = log.pointsDiff ?? 0;
        activities.push({
          type: "points",
          title:
            points > 0
              ? `+${points} ${pointType?.name || this.$t("profile.points")}`
              : `${points} ${pointType?.name || this.$t("profile.points")}`,
          subtitle: log.concept || this.$t("profile.points-updated"),
          icon: points > 0 ? "PlusRound" : "MinusRound",
          color: points > 0 ? this.accentColor : "#EF4444",
          timeAgo: this.getTimeAgo(log.creationTime),
          date: new Date(log.creationTime),
          tags: [],
        });
      });

      // Add applied promotions
      this.appliedPromotions.slice(0, 3).forEach((ap) => {
        const promotion = this.promotionsById[ap.promotionId];
        activities.push({
          type: "promotion",
          title: this.$t("profile.coupon-redeemed"),
          subtitle: promotion?.name || "",
          icon: "CardGiftcardRound",
          color: "#2DD4BF",
          timeAgo: this.getTimeAgo(ap.creationTime),
          date: new Date(ap.creationTime),
          tags: [],
        });
      });

      // Sort by date
      return activities.sort((a, b) => b.date - a.date).slice(0, 5);
    },
  },
  methods: {
    ...mapActions(authStore, ["updateCurrentCustomer"]),
    async fetchCustomerProfile() {
      this.loading = true;
      try {
        const response = await api.get(`/customer/${this.customerId}/profile`);
        this.customer = response.data.customer;
        this.customerPoints = response.data.customerPoints || [];
        this.customerPointsLog = response.data.customerPointsLog || [];
        this.customerLevel = response.data.customerLevel;
        this.appliedPromotions = response.data.appliedPromotions || [];
        this.assignedPromotions = response.data.assignedPromotions || [];
        this.visits = response.data.visits || 0;
        this.pointTypes = response.data.pointTypes || [];
        this.promotions = response.data.promotions || [];
        // Update the auth store with the customer so download page can access it
        this.updateCurrentCustomer(this.customer);
      } catch (e) {
        this.removeCustomerInfoFromOrganization(this.organization.id);
        this.$router.replace({ name: "customerHub" });
      } finally {
        this.loading = false;
      }
    },
    getTimeAgo(date) {
      return moment(date).fromNow();
    },
    handleDownloadPass() {
      this.$router.push({
        name: "downloadPage",
        params: {
          shortenedSignupPageId: this.$route.params.shortenedSignupPageId,
        },
      });
    },
    goToHub() {
      this.$router.push({
        name: "customerHub",
        params: {
          shortenedSignupPageId: this.$route.params.shortenedSignupPageId,
        },
      });
    },
    handleOrderAdded() {
      this.fetchCustomerProfile();
    },
    openEditModal() {
      this.editForm = {
        name: this.customer.name || "",
        surname: this.customer.surname || "",
        birthDate: this.customer.birthDate
          ? new Date(this.customer.birthDate).getTime()
          : null,
        email: this.customer.email || "",
        phoneNumber: this.customer.phoneNumber || "",
        marketingCommunication: !!this.customer.marketingCommunication,
      };
      this.showEditModal = true;
    },
    async saveProfile() {
      if (!this.editForm.name?.trim()) return;
      this.saving = true;
      try {
        let payload = {
          name: this.editForm.name.trim(),
          surname: this.editForm.surname?.trim() || null,
          birthDate: this.editForm.birthDate
            ? new Date(this.editForm.birthDate).toISOString()
            : null,
          email: this.editForm.email?.trim() || null,
          phoneNumber: this.editForm.phoneNumber?.trim() || null,
          marketingCommunication: this.editForm.marketingCommunication,
        };
        await api.put(`/customer/${this.customerId}/profile`, payload);
        await this.fetchCustomerProfile();
        this.showEditModal = false;
      } catch (e) {
        let errorCode = e?.response?.data?.error;
        if (errorCode === "email_exists") {
          this.$notificationGenerator.error(
            this.$t("profile.email-already-in-use")
          );
        } else if (errorCode === "phone_exists") {
          this.$notificationGenerator.error(
            this.$t("profile.phone-already-in-use")
          );
        } else {
          this.$notificationGenerator.error();
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
